/**
 * Order management interface for admins
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useState, useEffect, useMemo } from 'react';
import { adminApi, ordersApi } from '../../services/api';
import { Order, PaginatedResponse } from '../../types';
import { FilterBar, FilterConfig } from '../shared/FilterBar';
import { Pagination } from '../shared/Pagination';
import { useAdminFilters } from './shared/hooks/useAdminFilters';
import { useSelection } from '../../hooks/useSelection';
import DataTable, { Column } from '../shared/DataTable';
import BulkActionToolbar, { BulkActionButton } from '../shared/BulkActionToolbar';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import {
  Eye,
  Undo2,
  ShoppingCart,
  CheckCircle,
  Clock,
  DollarSign,
  RefreshCw,
  HelpCircle,
} from 'lucide-react';

// Filter configuration for orders
const orderFilters: FilterConfig[] = [
  {
    type: 'search',
    label: 'Search',
    key: 'search',
    placeholder: 'Search order ID, customer email, payment ID...',
    width: 'lg',
  },
  {
    type: 'select',
    label: 'Status',
    key: 'status',
    width: 'md',
    options: [
      { value: '', label: 'All Statuses' },
      { value: 'pending', label: 'Pending' },
      { value: 'processing', label: 'Processing' },
      { value: 'completed', label: 'Completed' },
      { value: 'refunded', label: 'Refunded' },
      { value: 'failed', label: 'Failed' },
    ],
  },
  {
    type: 'number-range',
    label: 'Order Total',
    key: 'total',
    width: 'md',
  },
  {
    type: 'date-range',
    label: 'Order Date',
    key: 'date',
    width: 'md',
  },
];

export default function OrderManagement(): JSX.Element {
  const [response, setResponse] = useState<PaginatedResponse<Order> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Selection state for bulk actions
  const { selectedIds, toggleSelection, toggleAll, clearSelection, selectedCount } = useSelection();

  // Use the admin filters hook with URL synchronization
  const { filters, debouncedFilters, setFilter, clearFilters } = useAdminFilters({
    page: 1,
    limit: 25,
    search: '',
    status: '',
    totalMin: '',
    totalMax: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Load orders whenever debounced filters change
  useEffect(() => {
    loadOrders();
  }, [debouncedFilters]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      // Build API params from filters
      const params: any = {
        page: debouncedFilters.page,
        limit: debouncedFilters.limit,
        sortBy: debouncedFilters.sortBy,
        sortOrder: debouncedFilters.sortOrder,
      };

      if (debouncedFilters.search) params.search = debouncedFilters.search;
      if (debouncedFilters.status) params.status = debouncedFilters.status;
      if (debouncedFilters.totalMin) params.totalMin = Number(debouncedFilters.totalMin);
      if (debouncedFilters.totalMax) params.totalMax = Number(debouncedFilters.totalMax);
      if (debouncedFilters.dateFrom) params.dateFrom = debouncedFilters.dateFrom;
      if (debouncedFilters.dateTo) params.dateTo = debouncedFilters.dateTo;

      const data = await adminApi.listOrders(params);
      setResponse(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (orderId: string) => {
    if (!confirm('Are you sure you want to process a refund for this order?')) return;

    try {
      await ordersApi.refund(orderId);
      alert('Refund processed successfully');
      await loadOrders();
    } catch (error: any) {
      console.error('Failed to process refund:', error);
      alert(error.message || 'Failed to process refund');
    }
  };

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  const handleSort = (key: string) => {
    const newOrder = filters.sortBy === key && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilter('sortBy', key);
    setFilter('sortOrder', newOrder);
  };

  // Bulk action handlers
  const handleBulkRefund = async () => {
    if (bulkProcessing) return;

    try {
      setBulkProcessing(true);
      const result = await adminApi.bulkRefundOrders(Array.from(selectedIds));

      alert(
        `Refunded: ${result.success}\n` +
        `Skipped (locked/invalid): ${result.skipped}\n` +
        `Failed: ${result.failed}` +
        (result.errors.length > 0 ? `\n\nErrors:\n${result.errors.slice(0, 3).join('\n')}` : '')
      );

      clearSelection();
      await loadOrders();
    } catch (error) {
      console.error('Bulk refund error:', error);
      alert(`Failed to refund orders: ${error}`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleExportCSV = async () => {
    if (bulkProcessing) return;

    try {
      setBulkProcessing(true);
      const blob = await adminApi.exportOrdersCSV(Array.from(selectedIds));

      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-export-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      clearSelection();
    } catch (error) {
      console.error('Export error:', error);
      alert(`Failed to export orders: ${error}`);
    } finally{
      setBulkProcessing(false);
    }
  };

  // Helper function for status badge color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'processing':
        return 'blue';
      case 'refunded':
        return 'purple';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Table column definitions
  const columns: Column<Order>[] = [
    {
      key: 'id',
      header: 'Order',
      sortable: false,
      render: (order) => (
        <div>
          <div className="font-mono text-sm font-medium">#{order.id.slice(0, 8)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {order.createdAt && new Date(order.createdAt).toLocaleDateString()}
          </div>
        </div>
      ),
    },
    {
      key: 'userEmail',
      header: 'Customer',
      sortable: true,
      render: (order) => (
        <span className="text-sm">{order.userEmail}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (order) => (
        <span className={`px-2 py-1 text-xs font-medium rounded bg-${getStatusColor(order.status)}-100 text-${getStatusColor(order.status)}-800 dark:bg-${getStatusColor(order.status)}-900 dark:text-${getStatusColor(order.status)}-200`}>
          {order.status}
        </span>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      sortable: false,
      render: (order) => (
        <span className="text-sm">{order.items?.length || 0}</span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      sortable: true,
      render: (order) => (
        <span className="font-medium">
          {order.total?.currency || 'USD'} {(order.total?.amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: 'downloadLocked',
      header: 'Download',
      sortable: false,
      render: (order) => (
        order.downloadLocked
          ? <span className="text-xs px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded">Locked</span>
          : <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded">Available</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (order) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewDetails(order)}
            className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          {order.status === 'completed' && !order.downloadLocked && (
            <button
              onClick={() => handleRefund(order.id)}
              className="p-2 rounded bg-purple-600 hover:bg-purple-700 text-white transition-colors"
              title="Process Refund"
            >
              <Undo2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Bulk action buttons
  const bulkActions: BulkActionButton[] = [
    {
      label: 'Export CSV',
      onClick: handleExportCSV,
      variant: 'primary',
      disabled: bulkProcessing,
    },
    {
      label: 'Bulk Refund',
      onClick: handleBulkRefund,
      variant: 'danger',
      requireConfirmation: true,
      confirmMessage: `Refund ${selectedCount} orders? Only completed orders without downloads will be processed.`,
      disabled: bulkProcessing,
    },
  ];

  // These must be BEFORE any early return to satisfy React's rules of hooks
  const orders = response?.items || [];
  const pagination = response?.pagination;

  // Calculate stats from current data
  const stats = useMemo(() => {
    const completed = orders.filter(o => o.status === 'completed').length;
    const pending = orders.filter(o => o.status === 'pending' || o.status === 'processing').length;
    const totalRevenue = orders
      .filter(o => o.status === 'completed')
      .reduce((sum, o) => sum + (o.total?.amount || 0), 0);

    return {
      total: pagination?.total || orders.length,
      completed,
      pending,
      revenue: totalRevenue,
    };
  }, [orders, pagination]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'total',
      label: 'Total Orders',
      value: stats.total,
      icon: ShoppingCart,
      variant: 'primary',
    },
    {
      id: 'completed',
      label: 'Completed',
      value: stats.completed,
      icon: CheckCircle,
      variant: 'success',
    },
    {
      id: 'pending',
      label: 'Pending',
      value: stats.pending,
      icon: Clock,
      variant: 'warning',
    },
    {
      id: 'revenue',
      label: 'Revenue (Page)',
      value: `$${stats.revenue.toFixed(2)}`,
      icon: DollarSign,
      variant: 'default',
    },
  ], [stats]);

  // Early return AFTER all hooks have been called
  if (selectedOrder) {
    return (
      <OrderDetails
        order={selectedOrder}
        onClose={handleCloseDetails}
        onRefund={handleRefund}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Order Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            View, manage, and process customer orders
          </p>
        </div>
        <button
          onClick={loadOrders}
          disabled={loading}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Section */}
      <section>
        <StatsWidget stats={statItems} columns={4} loading={loading} />
      </section>

      {/* Filters */}
      <FilterBar
        filters={orderFilters}
        values={filters}
        onChange={setFilter}
        onClear={clearFilters}
        resultsCount={pagination?.total}
      />

      {/* Order Table */}
      <div className="card">
        <DataTable
          data={orders}
          columns={columns}
          selectedIds={selectedIds}
          onSelectChange={toggleSelection}
          onSelectAll={toggleAll}
          onSort={handleSort}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder as 'asc' | 'desc'}
          idKey="id"
          loading={loading}
          emptyMessage={orders.length === 0 && !loading ? 'No orders found' : ''}
        />
      </div>

      {/* Pagination */}
      {!loading && pagination && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setFilter('page', page)}
          onItemsPerPageChange={(limit) => setFilter('limit', limit)}
        />
      )}

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help with orders?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn about order processing, refunds, and customer management
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}

/**
 * Order details view
 */
interface OrderDetailsProps {
  order: Order;
  onClose: () => void;
  onRefund: (orderId: string) => void;
}

function OrderDetails({ order, onClose, onRefund }: OrderDetailsProps): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Order Details</h2>
        <button onClick={onClose} className="btn btn-secondary">
          ← Back to List
        </button>
      </div>

      {/* Order Info */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4">
          <div>
            <h3 className="text-xl font-bold">Order #{order.id}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {new Date(order.createdAt.seconds * 1000).toLocaleString()}
            </p>
          </div>
          <span
            className={`px-3 py-1 text-sm font-medium rounded ${
              order.status === 'completed'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : order.status === 'refunded'
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                : order.status === 'failed'
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
            }`}
          >
            {order.status.toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Customer</p>
            <p className="font-medium">{order.userEmail}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">User ID</p>
            <p className="font-medium text-sm">{order.userId}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Payment Provider</p>
            <p className="font-medium capitalize">{order.paymentProvider}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Payment Intent ID</p>
            <p className="font-medium text-sm">{order.paymentIntentId}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Download Status</p>
            <p className={`font-medium ${order.downloadLocked ? 'text-red-600' : 'text-green-600'}`}>
              {order.downloadLocked ? '🔒 Locked (Downloaded)' : '✅ Available'}
            </p>
          </div>
          {order.completedAt && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Completed At</p>
              <p className="font-medium text-sm">
                {new Date(order.completedAt.seconds * 1000).toLocaleString()}
              </p>
            </div>
          )}
          {order.refundedAt && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Refunded At</p>
              <p className="font-medium text-sm">
                {new Date(order.refundedAt.seconds * 1000).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="card p-6">
        <h3 className="text-lg font-bold mb-4">Order Items</h3>
        <div className="space-y-3">
          {order.items.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
            >
              <div className="flex-1">
                <p className="font-medium">{item.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Quantity: {item.quantity}
                </p>
              </div>
              <p className="font-medium">
                {item.price.currency.toUpperCase()} {item.price.amount.toFixed(2)}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-lg font-bold">
            <span>Total</span>
            <span className="text-primary-800 dark:text-primary-400">
              {order.total.currency.toUpperCase()} {order.total.amount.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      {order.status === 'completed' && !order.downloadLocked && (
        <div className="card p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700">
          <h3 className="font-bold text-yellow-800 dark:text-yellow-400 mb-2">
            ⚠️ Refund Available
          </h3>
          <p className="text-sm text-yellow-800 dark:text-yellow-400 mb-4">
            This order has not been downloaded yet. You can process a refund.
          </p>
          <button
            onClick={() => onRefund(order.id)}
            className="btn bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            💰 Process Refund
          </button>
        </div>
      )}

      {order.downloadLocked && (
        <div className="card p-6 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700">
          <h3 className="font-bold text-red-800 dark:text-red-400 mb-2">
            🔒 Refund Locked
          </h3>
          <p className="text-sm text-red-800 dark:text-red-400">
            This order has been downloaded and refunds are no longer available.
          </p>
        </div>
      )}
    </div>
  );
}
